/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2020, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

const SMARTS_TO_DASHBOARD_AGGREGATION_TYPE_MAP = {
	SUM: 'sum',
	COUNT_DISTINCT: 'countdistinct',
	COUNT: 'count',
	AVERAGE: 'avg',
	NONE: 'none',
	MINIMUM: 'min',
	MAXIMUM: 'max'
}

export default function VisFilterConverter() {
	return {
		initialize(glassContext) {
			this._glassContext = glassContext
		},
		/**
		 * Add convert vis filters and modifiers to spec filters
		 * @param chartRecommendation
		 * @param spec
		 * @returns updated spec
		 * @private
		 */
		convertVisFiltersToLiveWidgetSpec(spec, chartRecommendation) {

			const aggregations = {}
			const topBottomFilterMap = {}
			const { filters, columns } = chartRecommendation

			const FILTER_TYPES = {
				INCLUSION_EXCLUSION: 'INCLUSION_EXCLUSION',
				TOP_BOTTOM: 'TOP_BOTTOM',
				NUMERIC: 'NUMERIC',
				NUMERIC_RANGE: 'NUMERICRANGE'
			}

			if (columns) {
				columns.forEach(column => {
					if (column.aggregation) {
						column.id.forEach(id => aggregations[id] = column.aggregation)
					}
				})
			}

			spec.localFilters = []

			if (filters) {
				filters.forEach((filter, i) => {
					filter.filterId = [filter.columnId, i].join('_');
					switch(filter.type) {
						case FILTER_TYPES.INCLUSION_EXCLUSION:
							spec.localFilters.push( this._addInclusionExclusionFilter(filter) )
							break
						case FILTER_TYPES.TOP_BOTTOM:
							topBottomFilterMap[filter.columnId] = filter
							break
						case FILTER_TYPES.NUMERIC:
							spec.localFilters.push( this._addNumericFilter(filter) )
							break
						case FILTER_TYPES.NUMERIC_RANGE:
							spec.localFilters.push( this._addNumericRangeFilter(filter) )
							break
						default:
						// Filter type is not recognized - Do nothing.
							break
					}

				})
			}

			if (spec.data) {
				const dataViews = spec.data.dataViews

				dataViews.forEach(dataView => {
					const dataItems = dataView.dataItems

					dataItems.forEach(dataItem => {
						const { itemId } = dataItem

						if (aggregations[itemId]) {
							dataItem = this._addAggregateSpec(dataItem, aggregations[itemId])
						}

						if (topBottomFilterMap && topBottomFilterMap[itemId]) {
							dataItem = this._addTopBottomSpec(dataItem, topBottomFilterMap[itemId])
						}
					})

				})
			}

			return spec
		},

		_addAggregateSpec(dataItem, aggregation) {
			dataItem.aggregate = SMARTS_TO_DASHBOARD_AGGREGATION_TYPE_MAP[aggregation]

			return dataItem
		},

		_addNumericFilter(filter) {
			const OPERATOR_TYPES = {
				'GREATER_THAN': 'gt',
				'LESS_THAN': 'lt',
				'GREATER_THAN_EQUALS': 'gte',
				'LESS_THAN_EQUALS': 'lte',
				'EQUALS': 'between',
				'NOT_EQUALS': 'notbetween'
			}
			let localFilterId = filter.filterId ? filter.filterId : filter.columnId
			let preOrPostValue = (filter.filterAggregation && filter.filterAggregation.preOrPostAggregation)?filter.filterAggregation.preOrPostAggregation.toLowerCase() : 'pre'

			const localFilter = {
				id: localFilterId,
				columnId: filter.columnId,
				values: [
					{
						d: filter.value, // Display value
						u: filter.value // Unique value
					}
				],
				excludedValues: [],
				operator: OPERATOR_TYPES[filter.operator],
				preOrPost: preOrPostValue
			}

			if(filter.filterAggregation && filter.filterAggregation.preOrPostAggregation && filter.filterAggregation.aggregationType){
				localFilter.aggregationType = SMARTS_TO_DASHBOARD_AGGREGATION_TYPE_MAP[filter.filterAggregation.aggregationType]
			}


			if (filter.operator == 'EQUALS' || filter.operator == 'NOT_EQUALS') {
				// add the same value as the upper bound value
				let value = {
					d: filter.value,
					u: filter.value
				}
				localFilter.values.push(value)
			}

			return localFilter
		},

		_addNumericRangeFilter(filter) {
			const RangeOperator = {
				BETWEEN: 'between',
				NOT_BETWEEN: 'notbetween'
			}

			const localFilterId = filter.filterId ? filter.filterId : filter.columnId
			const localFilter = {
				id: localFilterId,
				columnId: filter.columnId,
				values: [
					{
						d: filter.lowValue.value, // Display value
						u: filter.lowValue.value // Unique value
					},
					{
						d: filter.highValue.value, // Display value
						u: filter.highValue.value // Unique value
					}
				],
				excludedValues: [],
				operator: filter.invertFlag ? RangeOperator.NOT_BETWEEN : RangeOperator.BETWEEN,
				preOrPost: 'pre'
			}

			return localFilter
		},

		_addInclusionExclusionFilter(filter) {
			let { inclusionValues, exclusionValues, label, filterCondition } = filter
			let isInclude = inclusionValues && !!inclusionValues.length

			if (filterCondition) {
			// Inclusion and Exclusion values are currently empty when
			// a filter condition is applied so we handle it differently.
				return this._addInclusionExclusionFilterWithCondition(filter)
			}

			if (filter.invertFlag) {
				isInclude = !isInclude;

				if (isInclude) {
					inclusionValues = exclusionValues;
				} else {
					exclusionValues = inclusionValues;
				}
			}

			const values = isInclude ? inclusionValues : exclusionValues

			const localFilterId = filter.filterId ? filter.filterId : filter.columnId
			const localFilter = {
				id: localFilterId,
				columnId: filter.columnId,
				values: values.map( ({ displayValue:d, uniqueValue:u }) => ({ d, u })),
				operator: ( isInclude ) ? 'in' : 'notin',
				binsLabel : label
			}

			if (isInclude) {
				localFilter.excludedValues = []
			}

			return localFilter
		},

		_addInclusionExclusionFilterWithCondition({ filterId, columnId, filterCondition, invertFlag }) {
			const CONDITION_TYPES = {
				CONTAINS: 'containsignorecase',
				// Note: These are not currently supported by the Conversation Service
				// but may be added later.
				EQUALS: 'in',
				BEGINS_WITH: 'beginswith',
				ENDS_WITH: 'endswith'
			}

			let condition = {
				operator: CONDITION_TYPES[filterCondition.condition],
				itemId: columnId,
				ignoreCase: true,
				values: filterCondition.values,
				valueType: 'display'
			}

			if (filterCondition.values && filterCondition.values.length > 1) {
				const conditionWithOperator = { or: [] }
				filterCondition.values.forEach(value => {
					const tmpCondition = Object.assign({}, condition)
					tmpCondition.values = [value]
					conditionWithOperator.or.push(tmpCondition)
				})

				condition = conditionWithOperator
			}

			const localFilterId = filterId ? filterId : columnId
			const localFilter = {
				id: localFilterId,
				columnId: columnId,
				values: [],
				excludedValues: [],
				operator: 'in',
				conditions: invertFlag ? { not: condition } : condition
			}

			return localFilter
		},

		_addTopBottomSpec(dataItem, topBottomFilter) {
			let { topBottomType, method } = topBottomFilter
			topBottomType = topBottomType.toLowerCase()

			if( !topBottomType ){
				return dataItem
			}

			method = method.toLowerCase()

			let newType = topBottomType + (method === 'count' ? 'count' : 'percent')
			let topBottomSpec = {
				operation: 'keep',
				topBottom: {
					type: newType,
					value: topBottomFilter.domainSize
				}
			}

			if (topBottomFilter.byColumn) {
				let context = { itemId : topBottomFilter.byColumn }
				const aggregationType = topBottomFilter.byAggregate
				if( aggregationType ){
					context.aggregate = SMARTS_TO_DASHBOARD_AGGREGATION_TYPE_MAP[aggregationType]
				}
				topBottomSpec.context = [context]
			}

			dataItem.selection = [topBottomSpec]
			return dataItem
		}
	}
}
